/** API client with cookie auth (web) and Bearer token auth (native) */
import { Platform } from 'react-native';
import { config } from '../config';
import { APP_VERSION } from '../constants/appVersion';

export class ApiError extends Error {
  code?: string;
  /** Full JSON error body from the server (e.g. the 409 family-gate lists), when it had one. */
  details?: Record<string, unknown>;
  constructor(message: string, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.code = code;
    this.details = details;
    this.name = 'ApiError';
  }
}

/**
 * Thrown when the backend explicitly rejected our refresh token (401/403)
 * or a request 401'd even with a freshly-refreshed token — the session is
 * truly dead and tokens must be cleared.  Transient network failures during
 * a refresh are ordinary Errors/TypeErrors and must NOT clear tokens (the
 * refresh may even have succeeded server-side and rotated the token; wiping
 * our copy would orphan the session).
 */
export class SessionExpiredError extends Error {
  constructor(message = 'Session expired. Please sign in again.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

const API_URL = config.apiUrl;
const TOKEN_KEY = 'rinsepay_auth_token';
const REFRESH_TOKEN_KEY = 'rinsepay_refresh_token';
const IS_WEB = Platform.OS === 'web';

// In-memory token state (native only; web uses httpOnly cookies).
// These in-memory values are AUTHORITATIVE for the running session — the
// secure store is only a persistence layer for cold starts, and a failed
// keychain write must never break live requests (see compat/expo-secure-store).
let authToken: string | null = null;
let refreshToken: string | null = null;

// --- Session-death notification -------------------------------------------
// AuthContext subscribes so that when the client declares the session dead
// it can clear isAuthenticated/user state (mirroring logout's state reset
// without the logout API call).  Without this, navigation guards still see
// isAuthenticated=true and bounce the user from the welcome screen straight
// back into an authenticated screen, which fires a storm of 401ing fetches.

type SessionExpiredListener = () => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();

/** Subscribe to session death. Returns an unsubscribe function. */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

/**
 * Single place that tears down a dead session: clear tokens, notify
 * subscribers (AuthContext), then redirect to the welcome screen.
 * Idempotent — safe to call from multiple concurrent failed requests.
 */
async function handleSessionDeath(): Promise<void> {
  await clearToken();
  for (const listener of sessionExpiredListeners) {
    try { listener(); } catch { /* subscriber errors must not block teardown */ }
  }
  if (!IS_WEB) {
    try {
      const { router: expoRouter } = await import('expo-router');
      expoRouter.replace('/?expired=1');
    } catch { /* non-critical: router not available */ }
  }
  // Web: AuthContext's onSessionExpired handler performs the navigation
  // (it knows whether the user was actually signed in, avoiding redirect
  // loops from stale cookies on public pages).
}

// --- Token storage (native only) ---

async function getSecureStore() {
  if (IS_WEB) return null;
  try {
    return await import('expo-secure-store');
  } catch { /* expo-secure-store unavailable on this platform */
    return null;
  }
}

export async function loadToken() {
  if (IS_WEB) return; // Web uses httpOnly cookies, no token storage
  const store = await getSecureStore();
  if (store) {
    authToken = await store.getItemAsync(TOKEN_KEY);
    refreshToken = await store.getItemAsync(REFRESH_TOKEN_KEY);
  }
}

export async function saveToken(token: string) {
  if (IS_WEB) return; // Web tokens are in httpOnly cookies
  authToken = token;
  const store = await getSecureStore();
  if (store) await store.setItemAsync(TOKEN_KEY, token);
}

export async function saveRefreshToken(token: string) {
  if (IS_WEB) return; // Web tokens are in httpOnly cookies
  refreshToken = token;
  const store = await getSecureStore();
  if (store) await store.setItemAsync(REFRESH_TOKEN_KEY, token);
}

export async function clearToken() {
  authToken = null;
  refreshToken = null;
  if (IS_WEB) return; // Cookies cleared server-side on logout
  const store = await getSecureStore();
  if (store) {
    try { await store.deleteItemAsync(TOKEN_KEY); } catch { /* non-critical */ }
    try { await store.deleteItemAsync(REFRESH_TOKEN_KEY); } catch { /* non-critical */ }
  }
}

export function getToken() {
  return authToken;
}

export function getRefreshToken() {
  return refreshToken;
}

/** Read CSRF token from cookie (web only) */
export function getCsrfToken(): string | null {
  if (!IS_WEB || typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)rp_csrf=([^;]+)/);
  return match ? match[1] : null;
}

/**
 * Read the access-token expiry hint cookie set by the backend alongside
 * rp_access (see backend/src/utils/cookies.ts).  Returns the millisecond
 * epoch at which rp_access expires, or null if the cookie is missing
 * (not logged in, or running in an old build that didn't set it).
 *
 * Used to refresh proactively before firing a request, instead of
 * waiting for the request to 401 and then retrying.
 */
function getAccessExpiry(): number | null {
  if (!IS_WEB || typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)rp_access_exp=([^;]+)/);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : null;
}

/**
 * If the access cookie is missing/expired/about-to-expire AND we have a
 * csrf cookie (so we know we WERE logged in), refresh proactively.
 * Deduplicated through the single-flight refreshSession() so a burst of
 * parallel requests fires only one /api/auth/refresh.
 *
 * Web only — native uses the JWT-decode path in getValidToken.
 */
async function ensureFreshAccessWeb(): Promise<void> {
  if (!IS_WEB) return;
  if (!getCsrfToken()) return; // not logged in
  const exp = getAccessExpiry();
  // 30s buffer so we never fire a request that expires mid-flight
  if (exp !== null && exp > Date.now() + 30_000) return;
  // Failure → fall through; the request will 401 and the retry path takes
  // over (and declares session death there if the refresh was rejected).
  await refreshSession().catch(() => undefined);
}

/** Check if access token is expired or about to expire (native only) */
function isTokenExpired(): boolean {
  if (!authToken) return true;
  try {
    const payload = JSON.parse(atob(authToken.split('.')[1]));
    return !payload.exp || payload.exp * 1000 < Date.now() + 60000;
  } catch { /* malformed JWT — treat as expired */
    return true;
  }
}

/**
 * Perform one /api/auth/refresh round-trip.  NEVER call directly — always go
 * through refreshSession() so concurrent callers share a single flight (the
 * backend rotates refresh tokens atomically/single-use, so two concurrent
 * refreshes race and the loser invalidates the winner's session).
 *
 * Error contract:
 *  - server rejected the refresh token (401/403) → SessionExpiredError
 *  - any other failure (network TypeError, timeout, 5xx) → ordinary Error,
 *    tokens are LEFT INTACT so the next request can try again.
 */
async function doRefresh(): Promise<string> {
  if (IS_WEB) {
    // Web: refresh token is in httpOnly cookie, sent automatically
    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Client-Type': 'web' },
      credentials: 'include',
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      // 401/403 = rejected; 400 = no refresh cookie at all (expired and dropped by the browser) — either way
      // the session is dead and we must sign out instead of leaving "Missing or invalid authorization" on screen.
      if (response.status === 401 || response.status === 403 || response.status === 400) {
        throw new SessionExpiredError();
      }
      throw new Error(`Session refresh failed (${response.status}). Please try again.`);
    }
    return 'cookie'; // Token is in the cookie, not in the response
  }

  // Native: send refresh token in body
  if (!refreshToken) throw new SessionExpiredError('No refresh token');
  const response = await fetch(`${API_URL}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      // Token clearing + AuthContext notification happen in
      // handleSessionDeath(), invoked by whoever awaits refreshSession().
      throw new SessionExpiredError();
    }
    // Transient server-side failure — keep tokens, retry later.
    throw new Error(`Session refresh failed (${response.status}). Please try again.`);
  }
  const data = await response.json();
  await saveToken(data.token);
  await saveRefreshToken(data.refreshToken);
  return data.token;
}

/**
 * Single-flight refresh: EVERY refresh (proactive web, proactive native,
 * and both 401-retry paths) funnels through here.  While one refresh is in
 * flight, all other callers await the same promise instead of firing their
 * own — mandatory because the backend's refresh-token rotation is
 * single-use.
 */
let inflightRefresh: Promise<string> | null = null;
function refreshSession(): Promise<string> {
  if (!inflightRefresh) {
    inflightRefresh = doRefresh().finally(() => { inflightRefresh = null; });
  }
  return inflightRefresh;
}

/** Get a valid access token, refreshing if needed (native only) */
async function getValidToken(): Promise<string | null> {
  if (IS_WEB) return null; // Web uses cookies
  if (!authToken && !refreshToken) return null;
  if (!isTokenExpired()) return authToken;
  return refreshSession();
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  timeout?: number;
  skipAuth?: boolean;
}

const DEFAULT_TIMEOUT_MS = 30000;
const TRANSIENT_RETRY_DELAY_MS = 500;

/** True if err looks like a transient connectivity blip (DNS, dropped wifi, etc.)
 *  vs. a deliberate timeout (AbortError) or a real HTTP error.  Used to retry
 *  idempotent reads once before surfacing the failure to the caller. */
function isTransientNetworkError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  if (err.name === 'AbortError') return false; // user/timeout-initiated, don't auto-retry
  // RN: "Network request failed".  Web: "Failed to fetch" / "Load failed".
  return err instanceof TypeError || /network|fetch|load failed/i.test(err.message);
}

async function doFetch(path: string, options: RequestOptions, headers: Record<string, string>): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), options.timeout || DEFAULT_TIMEOUT_MS);
  try {
    return await fetch(`${API_URL}${path}`, {
      method: options.method || 'GET',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: controller.signal,
      ...(IS_WEB ? { credentials: 'include' as RequestCredentials } : {}),
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...options.headers };

  if (options.body) {
    headers['Content-Type'] = 'application/json';
  }

  // Native builds advertise their version so the backend version gate can
  // require/recommend an update. Web is always latest, so it sends nothing.
  if (!IS_WEB) {
    headers['X-App-Version'] = APP_VERSION;
    headers['X-App-Platform'] = Platform.OS;
  }

  if (IS_WEB) {
    // Web: identify as web client, send CSRF token for state-changing requests
    headers['X-Client-Type'] = 'web';
    const method = options.method || 'GET';
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const csrf = getCsrfToken();
      if (csrf) headers['X-CSRF-Token'] = csrf;
    }
    // Proactively refresh the access cookie if it's expired or about
    // to expire — eliminates the network-panel 401 noise on every fresh
    // page load when the user is still logged in via the long-lived
    // refresh + csrf cookies.  No-op on the login/refresh endpoints
    // themselves (skipAuth) and when not logged in (no csrf cookie).
    if (!options.skipAuth) {
      await ensureFreshAccessWeb();
    }
  } else if (!options.skipAuth) {
    // Native: use Bearer token
    try {
      const token = await getValidToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch (err) {
      if (err instanceof SessionExpiredError) {
        // Server explicitly rejected the refresh token — session is dead.
        await handleSessionDeath();
      }
      // Transient failure (network blip while refreshing): keep tokens and
      // surface the error; the next request will try the refresh again.
      throw err;
    }
  }

  const method = (options.method || 'GET').toUpperCase();
  const isIdempotent = method === 'GET' || method === 'HEAD';

  let response: Response;
  try {
    response = await doFetch(path, options, headers);
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Request timed out. Please check your connection and try again.');
    }
    // Single retry on transient network errors for idempotent reads.  Avoids
    // double-billing on POSTs and avoids retrying intentional aborts.
    if (isIdempotent && isTransientNetworkError(err)) {
      await new Promise(r => setTimeout(r, TRANSIENT_RETRY_DELAY_MS));
      try {
        response = await doFetch(path, options, headers);
      } catch (err2: unknown) {
        if (err2 instanceof Error && err2.name === 'AbortError') {
          throw new Error('Request timed out. Please check your connection and try again.');
        }
        throw err2;
      }
    } else {
      throw err;
    }
  }

  const data = await response.json();

  if (!response.ok) {
    if (response.status === 401) {
      if (IS_WEB) {
        // Only attempt refresh if user has a CSRF cookie (meaning they were logged in)
        const hasCsrf = getCsrfToken();
        if (hasCsrf) {
          let refreshed = false;
          try {
            await refreshSession(); // single-flight, shared with concurrent 401s
            refreshed = true;
          } catch (refreshErr) {
            if (refreshErr instanceof SessionExpiredError) {
              // Server rejected the refresh cookie — session truly dead.
              await handleSessionDeath();
            }
            // else: transient failure — keep session, throw original error below.
          }
          if (refreshed) {
            try {
              const retryResponse = await doFetch(path, options, headers);
              if (retryResponse.ok) return (await retryResponse.json()) as T;
              // Still 401 with a freshly-refreshed cookie → genuinely unauthorized.
              if (retryResponse.status === 401) await handleSessionDeath();
            } catch { /* transient network error on the retried fetch — keep
                         session (the refresh SUCCEEDED), throw original error. */ }
          }
        } else if (!options.skipAuth) {
          // No CSRF cookie yet an authenticated call 401'd — the app may still
          // believe it's signed in (stale AuthContext state after cookies were
          // cleared). Declare session death; on web this only notifies
          // AuthContext, which knows whether the user was actually signed in
          // and skips navigation on public pages (no redirect loops).
          await handleSessionDeath();
        }
      } else if (refreshToken) {
        // Native: try refresh + retry once before giving up on the session.
        // Only a server-side rejection (SessionExpiredError) kills the session;
        // a network blip on the refresh OR on the retried fetch keeps tokens
        // intact (the refresh may have succeeded and rotated the token —
        // clearing here would orphan a perfectly valid session).
        let newToken: string | null = null;
        try {
          newToken = await refreshSession(); // single-flight, shared with concurrent 401s
        } catch (refreshErr) {
          if (refreshErr instanceof SessionExpiredError) {
            await handleSessionDeath();
          }
          // else: transient failure — keep tokens, throw original error below.
        }
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          try {
            const retryResponse = await doFetch(path, options, headers);
            if (retryResponse.ok) return (await retryResponse.json()) as T;
            // 401 again after a fresh token → genuinely unauthorized.
            if (retryResponse.status === 401) await handleSessionDeath();
          } catch { /* transient network error on the retried fetch — keep
                       tokens (the refresh SUCCEEDED), throw original error. */ }
        }
      } else if (authToken) {
        // We had an access token but no refresh token; nothing to do but log out.
        await handleSessionDeath();
      } else if (!options.skipAuth) {
        // Token-less 401 on an authenticated call: both tokens are null (e.g.
        // keychain wipe or cleared by a concurrent failure) but the app still
        // thinks it's signed in. Without this the 401 fell through with no
        // sign-out, leaving the user stuck on screens that 401 forever.
        await handleSessionDeath();
      }
    }
    throw new ApiError(data.error || data.message || 'Request failed', data.code, data && typeof data === 'object' ? data : undefined);
  }

  return data as T;
}

// Convenience helpers
export async function apiGet<T = unknown>(path: string, options?: Omit<RequestOptions, 'method'>) {
  return api<T>(path, { ...options, method: 'GET' });
}

export async function apiPost<T = unknown>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) {
  return api<T>(path, { ...options, method: 'POST', body });
}

export async function apiPatch<T = unknown>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) {
  return api<T>(path, { ...options, method: 'PATCH', body });
}

export async function apiDelete<T = unknown>(path: string, options?: Omit<RequestOptions, 'method'>) {
  return api<T>(path, { ...options, method: 'DELETE' });
}
